import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  Validators,
} from "@angular/forms";

/**
 * Requires a dot-labeled domain (e.g. `.com`, `.us`) so the frontend's
 * notion of "valid email" is at least as strict as the backend's
 * class-validator `@IsEmail`. Angular's `Validators.email` alone accepts
 * `"jane@company"` (no dot-label), which the server 400s on — a value the
 * form declared valid failing the round-trip (known-pitfalls: *"Angular
 * `Validators.email` is looser than class-validator `@IsEmail`"*).
 */
const STRICT_EMAIL_DOMAIN_PATTERN = /@[^\s@]+\.[^\s@.]{2,}$/;

/**
 * Validates an email address using both Angular's built-in
 * `Validators.email` and {@link STRICT_EMAIL_DOMAIN_PATTERN}, so a value
 * accepted here is also accepted by a backend `@IsEmail` field. Drop-in
 * replacement for `Validators.email` in any control that posts to an
 * `@IsEmail` backend.
 *
 * Empty values (`null` / `undefined` / `""`) are valid, matching
 * `Validators.email`'s own convention — requiredness stays the job of
 * `Validators.required`, and templates that display `required` and `email`
 * errors independently never show both on an empty control.
 *
 * @param control - The control to validate.
 * @returns `null` when valid or empty; `{ email: true }` otherwise (reuses
 *   `Validators.email`'s error key so error-display templates need no
 *   change).
 */
export function strictEmailValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value: unknown = control.value;
  if (value == null || value === "") return null;
  if (Validators.email(control) !== null) return { email: true };
  const text = typeof value === "string" ? value : "";
  return STRICT_EMAIL_DOMAIN_PATTERN.test(text) ? null : { email: true };
}

/**
 * Whether a single value passes {@link strictEmailValidator} after
 * trimming. Unlike the validator (where empty defers to
 * `Validators.required`), a blank value here is `false` — this is the
 * submit-gate / per-token form of the check (e.g. CSV email-list segments,
 * prefill guards).
 *
 * @param value - The candidate email address (trimmed internally).
 * @returns `true` when non-blank and email-shaped.
 */
export function isStrictEmail(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return strictEmailValidator(new FormControl(trimmed)) === null;
}
